import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import FileUpload, { AuthImage } from "@/components/FileUpload";
import WebcamCapture from "@/components/WebcamCapture";
import { Stethoscope, Receipt } from "lucide-react";

export default function PatientNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [panels, setPanels] = useState([]);
  // Registration workflow — decides which prints auto-open on save. Loaded
  // from /hospital/settings/registration-workflow; defaults match the API.
  const [workflow, setWorkflow] = useState({ rx_online: true, print_sticker: true, print_prescription_card: false });
  const [form, setForm] = useState({
    name: "", gender: "male", dob: "", age: "", mobile: "", email: "", address: "",
    emergency_contact: "", guardian: "", referral_source: "Walk-in", category: "cash",
    panel_id: "",
    abha_number: "", abha_address: "", id_proof_type: "Aadhaar", id_proof_value: "",
    photo_file_id: "",
    branch_id: user.branch_id || "",
    doctor_id: "",
    auto_invoice: true,
    payment_mode: "cash",
    payment_reference: "",  // UPI txn no / Card last-4
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hospital/branches").then(({ data }) => {
      setBranches(data.items);
      if (!form.branch_id && data.items[0]) setForm(f => ({ ...f, branch_id: data.items[0].id }));
    });
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items || []));
    api.get("/panel/companies").then(({ data }) => setPanels((data.items || []).filter((p) => p.status !== "inactive"))).catch(() => {});
    api.get("/hospital/settings/registration-workflow")
      .then(({ data }) => setWorkflow(w => ({ ...w, ...data })))
      .catch(() => { /* fall back to defaults if the endpoint isn't reachable */ });
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const selectedDoctor = doctors.find(d => d.id === form.doctor_id) || null;
  const isFree = form.category === "free";
  // Resolve effective rate based on doctor's per-category matrix.
  const effectiveRate = (() => {
    if (!selectedDoctor) return 0;
    const base = Number(selectedDoctor.opd_rate) || 0;
    const rbc = selectedDoctor.opd_rate_by_category || {};
    if (isFree) return Number(rbc.free ?? 0) || 0;
    if (form.category === "panel" && form.panel_id) {
      const pv = rbc.panel?.[form.panel_id];
      return pv !== undefined && pv !== "" ? Number(pv) : base;
    }
    if (form.category === "cash") {
      return rbc.cash !== undefined && rbc.cash !== "" ? Number(rbc.cash) : base;
    }
    return base;
  })();
  const rate = effectiveRate;

  const save = async () => {
    if (!form.name || !form.mobile || !form.branch_id) {
      toast.error("Name, mobile and branch are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        panel_id: form.category === "panel" ? (form.panel_id || null) : null,
        consult_fee_override: form.auto_invoice ? Number(rate) : null,
      };
      const { data } = await api.post("/patients", payload);
      toast.success(`Patient registered — UHID ${data.uhid}`);

      // Registration workflow — open only the prints the hospital has enabled
      // in Settings → Registration workflow. Doctor name is passed on the URL
      // so the print pages don't have to look up users.
      const docName = selectedDoctor ? `Dr. ${selectedDoctor.name}` : "—";
      if (workflow.print_sticker) {
        window.open(`/print/opd-sticker/${data.id}?doctor=${encodeURIComponent(docName)}`, "_blank");
      }
      if (workflow.print_prescription_card) {
        window.open(`/print/prescription-card/${data.id}?doctor=${encodeURIComponent(docName)}`, "_blank");
      }
      // (workflow.rx_online is informational — the OPD console picks it up
      // when the doctor consults the visit; no auto-print needed here.)

      // If a registration receipt was generated, auto-open in a new tab.
      if (data.companion_invoice_id) {
        window.open(`/print/registration-receipt/${data.companion_invoice_id}`, "_blank");
      }
      // Go back to the patient list so the user can confirm the new record
      // and register the next one — do NOT auto-open the patient profile
      // (which would surface the "Start OPD visit" UI prematurely).
      navigate("/app/patients");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-24">
      <div>
        <div className="section-label">Front desk</div>
        <h1 className="font-heading text-3xl font-semibold">New patient registration</h1>
        <p className="mt-1 text-sm text-ink-muted">Branch-wise UHID is auto-generated. ABHA can be linked now or later from patient profile.</p>
      </div>

      <Card className="p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-4">Patient photo</div>
        <div className="flex items-start gap-6">
          <div className="h-28 w-28 overflow-hidden rounded-md border border-border bg-secondary">
            {form.photo_file_id ? <AuthImage fileId={form.photo_file_id} className="h-full w-full object-cover" data-testid="patient-photo-preview" /> : <div className="flex h-full w-full items-center justify-center text-xs text-ink-subtle">No photo</div>}
          </div>
          <div className="flex-1">
            <Tabs defaultValue="upload">
              <TabsList data-testid="patient-photo-tabs">
                <TabsTrigger value="upload" data-testid="patient-photo-tab-upload">Upload file</TabsTrigger>
                <TabsTrigger value="camera" data-testid="patient-photo-tab-camera">Camera</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="mt-3">
                <FileUpload category="patient_photo" label="Upload photo" onUploaded={(f) => set("photo_file_id", f.id)} />
              </TabsContent>
              <TabsContent value="camera" className="mt-3">
                <WebcamCapture onUploaded={(f) => set("photo_file_id", f.id)} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-4">Demographic</div>
        <div className="form-grid">
          <div><Label>Full name *</Label><Input data-testid="patient-name-input" value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div>
            <Label>Gender *</Label>
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger data-testid="patient-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={e => {
            const dob = e.target.value;
            const age = (() => {
              if (!dob) return "";
              const d = new Date(dob); if (isNaN(d.getTime())) return "";
              const today = new Date();
              let yrs = today.getFullYear() - d.getFullYear();
              const m = today.getMonth() - d.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < d.getDate())) yrs -= 1;
              return yrs >= 0 && yrs <= 130 ? String(yrs) : "";
            })();
            setForm(s => ({ ...s, dob, age: age || s.age }));
          }} data-testid="patient-dob-input" /></div>
          <div><Label>Age</Label><Input type="number" value={form.age} onChange={e => set("age", e.target.value)} data-testid="patient-age-input" /></div>
          <div><Label>Mobile *</Label><Input data-testid="patient-mobile-input" value={form.mobile} onChange={e => set("mobile", e.target.value)} placeholder="+91-9xxxxxxxxx" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e => set("address", e.target.value)} /></div>
          <div><Label>Emergency contact</Label><Input value={form.emergency_contact} onChange={e => set("emergency_contact", e.target.value)} /></div>
          <div><Label>Guardian</Label><Input value={form.guardian} onChange={e => set("guardian", e.target.value)} /></div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-4">Visit · Doctor · Branch</div>
        <div className="form-grid">
          <div>
            <Label>Branch *</Label>
            <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}>
              <SelectTrigger data-testid="patient-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Stethoscope size={12} className="text-brand" />Consulting doctor</Label>
            <Select value={form.doctor_id || "__none__"} onValueChange={(v) => set("doctor_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="patient-doctor-select"><SelectValue placeholder="Select doctor (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Walk-in (no doctor)</SelectItem>
                {doctors.map(d => <SelectItem key={d.id} value={d.id}>Dr. {d.name}{d.opd_rate ? ` — ₹${d.opd_rate}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedDoctor && (
              <div className="mt-1.5 text-[11px] text-ink-muted" data-testid="patient-doctor-rate">
                OPD rate: <b className="text-brand" data-testid="patient-effective-rate">₹ {rate}</b>
                {selectedDoctor.qualification ? ` · ${selectedDoctor.qualification}` : ""}
                {rate !== (selectedDoctor.opd_rate || 0) && (
                  <span className="ml-1 rounded-sm bg-brand/10 px-1 py-0.5 text-[10px] text-brand">
                    {form.category === "panel" && form.panel_id ? "panel rate" : form.category + " rate"}
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => { set("category", v); if (v !== "panel") set("panel_id", ""); }}>
              <SelectTrigger data-testid="patient-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["cash","panel","insurance","corporate","camp","free"].map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.category === "panel" && (
            <div>
              <Label>Panel / TPA</Label>
              <Select value={form.panel_id || "__none__"} onValueChange={(v) => set("panel_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="patient-panel-select"><SelectValue placeholder="Select panel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— select —</SelectItem>
                  {panels.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {panels.length === 0 && (
                <div className="mt-1 text-[10px] text-amber-700">No active panels yet — add them under Panel &amp; insurance.</div>
              )}
            </div>
          )}
          <div><Label>Referral source</Label><Input value={form.referral_source} onChange={e => set("referral_source", e.target.value)} /></div>

          {form.doctor_id && (
            <>
              <div className="md:col-span-2 flex items-center gap-2 rounded-md border border-leaf/30 bg-leaf-50 p-3">
                <Checkbox id="auto-invoice" checked={form.auto_invoice} onCheckedChange={(v) => set("auto_invoice", !!v)} data-testid="patient-auto-invoice" />
                <Label htmlFor="auto-invoice" className="cursor-pointer text-xs">
                  <Receipt size={12} className="mr-1 inline" />
                  {isFree
                    ? "Auto-generate FREE-category OPD registration receipt (₹ 0) and print on save"
                    : `Auto-generate OPD registration receipt (₹ ${rate}) and print on save`}
                </Label>
              </div>
              {form.auto_invoice && !isFree && (
                <>
                  <div>
                    <Label>Payment mode</Label>
                    <Select value={form.payment_mode} onValueChange={(v) => { set("payment_mode", v); set("payment_reference", ""); }}>
                      <SelectTrigger data-testid="patient-payment-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.payment_mode === "upi" && (
                    <div>
                      <Label>UPI transaction no.</Label>
                      <Input value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} placeholder="e.g. 23467589XYZ" data-testid="patient-upi-txn" />
                    </div>
                  )}
                  {form.payment_mode === "card" && (
                    <div>
                      <Label>Card — last 4 digits</Label>
                      <Input maxLength={4} value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="0000" data-testid="patient-card-last4" />
                    </div>
                  )}
                </>
              )}
              {form.auto_invoice && isFree && (
                <div className="md:col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-700" data-testid="patient-free-banner">
                  <Receipt size={12} className="mr-1 inline" /> Free category — no amount will be charged; receipt prints as <b>FREE — ₹ 0</b>.
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-4">ABHA / Identity</div>
        <div className="form-grid">
          <div><Label>ABHA Number</Label><Input value={form.abha_number} onChange={e => set("abha_number", e.target.value)} placeholder="14-digit ABHA" /></div>
          <div><Label>ABHA Address</Label><Input value={form.abha_address} onChange={e => set("abha_address", e.target.value)} placeholder="e.g. rohit@sbx" /></div>
          <div>
            <Label>ID Proof Type</Label>
            <Select value={form.id_proof_type} onValueChange={(v) => set("id_proof_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Aadhaar","PAN","Voter","Passport","Driving License"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>ID Proof Value</Label><Input value={form.id_proof_value} onChange={e => set("id_proof_value", e.target.value)} /></div>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)] md:-mx-6 lg:-mx-8">
        <div className="text-xs text-ink-muted">All entries are time-stamped and audit-logged · NABH compliant</div>
        <Button data-testid="patient-save-btn" disabled={saving} onClick={save} className="bg-brand hover:bg-brand-700">{saving ? "Saving…" : "Register patient"}</Button>
      </div>
    </div>
  );
}
